// One assignment's completion state as the server evaluated it (GET
// /assignments/{id}/completion): the policy mode, each requirement met/unmet
// with the server's detail, the evidence/score on file, sign-offs, and the
// "overridden by an administrator" note when a force-complete happened, and
// (Phase 6) the platform events that verified the assignment. Nothing here
// decides whether a requirement is met — it only renders `state`.
import { useT } from '../lib/i18n';
import React from 'react';
import { CheckCircle2, Circle, ExternalLink, ShieldAlert, XCircle, Zap } from 'lucide-react';
import type { CompletionView } from '@/services/learningService';
import { useSystemEvents } from '../api/hooks';
import { modeLabel, requirementLabel, systemEventLabel } from '../lib/completion';

interface Props {
  view: CompletionView;
  className?: string;
  compact?: boolean;
  /** learning:manage | learning:assign — may read the event catalog (labels); learners see the event type humanized. */
  canReadCatalog?: boolean;
}

export const CompletionChecklist: React.FC<Props> = ({ view, className, compact, canReadCatalog }) => {
  const t = useT();
  const state = view.state;
  const reqs = state?.requirements || [];
  const hasPolicy = !!view.policy || reqs.length > 0;
  const verifications = view.system_verifications || [];
  const events = useSystemEvents(!!canReadCatalog && verifications.length > 0);
  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="completion-checklist">
      {hasPolicy ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {view.policy?.name && <span className="font-medium text-foreground">{view.policy.name}</span>}
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid="policy-mode">{modeLabel(t, state?.mode || view.policy?.mode)}</span>
            {state && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${state.satisfied ? 'bg-success-soft text-success-strong' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.16] dark:text-amber-300'}`} data-testid="policy-satisfied" data-satisfied={state.satisfied}>
                {state.satisfied ? t('training:policy_satisfied', 'Requirements met') : t('training:policy_unsatisfied', 'Not yet met')}
              </span>
            )}
          </div>
          <ul className="space-y-1">
            {reqs.map((r) => (
              <li key={r.kind} className="flex items-start gap-2 text-sm" data-testid={`req-${r.kind}`} data-met={r.met}>
                {r.met
                  ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-strong" aria-label={t('training:req_met', 'Met')} />
                  : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label={t('training:req_unmet', 'Not met')} />}
                <div className="min-w-0">
                  <div className="text-foreground">
                    {requirementLabel(t, r.kind)}
                    {r.threshold != null && <span className="text-xs text-muted-foreground"> · ≥ {r.threshold}%</span>}
                  </div>
                  {r.detail && <div className="text-xs text-muted-foreground">{r.detail}</div>}
                </div>
              </li>
            ))}
          </ul>
          {!compact && state?.evaluated_at && <div className="text-[11px] text-muted-foreground">{t('training:evaluated_at', 'Evaluated {{when}}', { when: new Date(state.evaluated_at).toLocaleString() })}</div>}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{t('training:no_policy', 'No completion requirements — mark complete when you are done.')}</p>
      )}

      {verifications.length > 0 && (
        <div className="space-y-0.5" data-testid="system-verifications">
          <div className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground"><Zap className="h-3 w-3" aria-hidden /> {t('training:system_verified_title', 'Verified by platform activity')}</div>
          <ul className="space-y-0.5 text-xs">
            {verifications.map((v) => (
              <li key={v.id} className="text-foreground" data-testid={`system-verification-${v.event_type}`}>
                <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-success-strong" aria-hidden />
                {systemEventLabel(events.data, v.event_type)}
                <span className="text-muted-foreground"> · {new Date(v.occurred_at).toLocaleString()} · {v.source_service}{v.aggregate_id ? ` · ${t('training:system_ref', 'ref')} ${v.aggregate_id}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(view.evidence_url || view.score != null) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="evidence-summary">
          {view.evidence_url && (
            <a className="inline-flex items-center gap-1 text-orange-600 hover:underline" href={view.evidence_url} target="_blank" rel="noreferrer">
              {t('training:view_evidence', 'View evidence')} <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          )}
          {view.score != null && <span>{t('training:score_n', 'Score: {{n}}', { n: view.score })}</span>}
        </div>
      )}

      {(view.signoffs || []).length > 0 && (
        <div className="space-y-0.5" data-testid="signoffs">
          <div className="text-[11px] font-medium text-muted-foreground">{t('training:signoffs', 'Sign-offs')}</div>
          <ul className="space-y-0.5 text-xs">
            {view.signoffs.map((s, i) => (
              <li key={`${s.kind}-${i}`} className="text-foreground">
                <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-success-strong" aria-hidden />
                {s.kind === 'manager' ? t('training:signoff_manager', 'Manager') : t('training:signoff_trainer', 'Trainer')} · {s.signed_by}
                <span className="text-muted-foreground"> · {new Date(s.signed_at).toLocaleString()}{s.note ? ` · ${s.note}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {view.overridden_by && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300" data-testid="overridden">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{t('training:overridden_by_admin', 'Overridden by an administrator')} · {view.overridden_by}</span>
        </div>
      )}
      {!view.overridden_by && view.status === 'cancelled' && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground"><XCircle className="h-3.5 w-3.5" aria-hidden /> {t('training:status_cancelled', 'cancelled')}</div>
      )}
    </div>
  );
};

export default CompletionChecklist;
